import { Link } from "react-router-dom";
import { formatEuro } from "@/lib/format";
import { btwAmount, btwLabel, useBtwPreference } from "@/lib/btw";
import { usePromotions, usePromotionsReady } from "@/lib/promotions";
import { useCatalogOverrides } from "@/lib/catalog-overrides";
import { ProductRatingBadge } from "@/components/ProductRatingBadge";
import { resolveFeatured } from "./FeaturedRow";
import { useT } from "@/lib/i18n";
import { cdnImage, IMG } from "@/lib/img";

/**
 * Homepage-sectie "Nu in de aanbieding" (navy blok). Vult zichzelf AUTOMATISCH met de
 * producten die een lopende korting hebben (van/voor) — geen admin-curatie. Zet je in de
 * admin (Prijsacties) een korting op een product, dan verschijnt het hier vanzelf.
 */
export function FeaturedDeals() {
  const t = useT();
  const [pref] = useBtwPreference();
  const ready = usePromotionsReady();
  const items = resolveFeatured(usePromotions(), useCatalogOverrides(), () => true, 100)
    .filter((it) => it.wasCents != null)
    .slice(0, 10);

  if (!ready) {
    // Skelet in dezelfde vorm/hoogte als de echte sectie: zonder dit verschijnt "Nu in de
    // aanbieding" pas ná de idle-Firestore-read en schuift alles eronder (kennisbank, reviews,
    // FAQ) in één keer mee omlaag — de grootste Cumulative-Layout-Shift-bron op de homepage.
    return (
      <section aria-hidden="true" className="mx-auto max-w-[1600px] px-4 pt-14">
        <div className="h-6 w-56 animate-pulse rounded bg-primary-100 sm:h-8 sm:w-72" />
        <div className="mt-6 rounded-2xl bg-[#013878] px-3 py-6 sm:px-5 sm:py-10">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex flex-col rounded-xl bg-white p-4">
                <div className="mb-3 aspect-square w-full animate-pulse rounded-lg bg-primary-100" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-primary-100" />
                <div className="mt-2 h-4 w-1/2 animate-pulse rounded bg-primary-50" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  if (items.length === 0) return null;

  return (
    <section aria-labelledby="uitgelicht-deals" className="mx-auto max-w-[1600px] px-4 pt-14">
      <h2 id="uitgelicht-deals" className="text-lg font-bold text-primary-900 sm:text-2xl">{t("Nu in de aanbieding")}</h2>
      <p className="mt-2 hidden max-w-2xl text-primary-600 sm:block">
        {t("Producten met een lopende korting — direct de juiste prijs, verzekerd geleverd.")}
      </p>
      <div className="mt-6 rounded-2xl bg-[#013878] px-3 py-6 sm:px-5 sm:py-10">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {items.map((it) => (
            <Link
              key={it.slug}
              to={it.to}
              className="group flex flex-col rounded-xl bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="relative mb-3 aspect-square w-full">
                {it.image ? (
                  // Productnaam als alt: een productfoto is inhoud, geen versiering — zonder
                  // beschrijving slaat een schermlezer 'm over en kan Google Afbeeldingen er
                  // niets mee. Komt automatisch mee met elk nieuw actie-artikel.
                  <img src={cdnImage(it.image, { w: IMG.card })} alt={it.title} width={IMG.card} height={IMG.card} loading="lazy" decoding="async" className="h-full w-full object-contain" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-primary-200">{t("geen foto")}</div>
                )}
                {it.pct != null && (
                  <span className="absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-full bg-accent-700 text-xs font-bold text-white shadow-sm">−{it.pct}%</span>
                )}
              </div>
              {it.chip && <span className="inline-block w-max rounded bg-accent-700 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-white">{t(it.chip)}</span>}
              {/* Bovenaan: titel → categorie → reviews. */}
              {it.title && <h3 className="mt-2 line-clamp-2 font-bold leading-tight text-primary-900 transition group-hover:text-primary-700">{t(it.title)}</h3>}
              {it.subtitle && <p className="mt-1 line-clamp-1 text-sm text-primary-600">{t(it.subtitle)}</p>}
              {!it.hideReviews && <div className="mt-1.5"><ProductRatingBadge slug={it.slug} /></div>}
              {/* Prijs onderaan (mt-auto = prijzen lijnen uit over de kaarten). */}
              <div className="mt-auto pt-2 text-sm">
                {it.hidePrice ? null : it.kind === "glas" && it.vanafCents != null ? (
                  <span className="text-primary-700">{t("vanaf")} <strong className="text-primary-900">{formatEuro(btwAmount(it.vanafCents, pref))}</strong>/m² <span className="text-[11px] font-normal text-primary-600">{btwLabel(pref)}</span></span>
                ) : it.vanafCents != null ? (
                  <span className="text-primary-700">{t("vanaf")} <strong className="text-primary-900">{formatEuro(btwAmount(it.vanafCents, pref))}</strong> <span className="text-[11px] font-normal text-primary-600">{btwLabel(pref)}</span></span>
                ) : it.onRequestEff ? (
                  <span className="font-semibold text-primary-700">{t("Prijs op aanvraag")}</span>
                ) : (
                  <span>
                    {it.wasCents != null && <span className="mr-1.5 text-primary-600 line-through">{formatEuro(btwAmount(it.wasCents, pref))}</span>}
                    <strong className={`text-base ${it.wasCents != null ? "text-accent-700" : "text-primary-900"}`}>{formatEuro(btwAmount(it.priceCents, pref))}</strong>
                    <span className="ml-1 text-[11px] font-normal text-primary-600">{btwLabel(pref)}</span>
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
